/**
 * Single source for workout bar strip geometry and colors (RunningWorkoutViz + graphic editor).
 * Height rules align with flattenStructureForViz / runningWorkoutFlatten when cp_percent is set.
 */

/** Max %CP represented in the graphic editor intensity band (headroom above 100% CP). */
export const GRAPHIC_EDITOR_CP_MAX = 150;

const DEFAULT_THRESHOLD_PACE = 300;

export function splitWeightPace(s, thresholdPaceSecPerKm) {
  const base = thresholdPaceSecPerKm > 0 ? thresholdPaceSecPerKm : DEFAULT_THRESHOLD_PACE;
  const ic = (s.run_intensity_class || 'work').toLowerCase();
  if (ic === 'rest') return base * 1.45;
  const cp = graphicEditorEffectiveCp(s);
  if (cp <= 0) return base * 1.22;
  return base * (100 / Math.max(45, Math.min(150, cp)));
}

/**
 * Bar width weight — always in "estimated seconds" so distance and duration bars are comparable.
 * @param {object} s — viz split
 * @param {number} [thresholdPaceSecPerKm] — user's threshold pace (sec per km); uses 5:00/km default
 */
export function splitWeight(s, thresholdPaceSecPerKm) {
  const mode = (s.split_measure_mode || 'duration').toLowerCase() === 'distance' ? 'distance' : 'duration';
  if (mode === 'distance') {
    const dm = s.distance_m == null || s.distance_m === '' ? 0 : Number(s.distance_m);
    if (Number.isFinite(dm) && dm > 0) {
      const pace = splitWeightPace(s, thresholdPaceSecPerKm);
      return Math.max(1, Math.round((dm / 1000) * pace));
    }
  }
  const d = Number(s.duration_sec) || 0;
  if (d > 0) return d;
  if (s.distance_m != null && Number(s.distance_m) > 0) {
    const dm = Number(s.distance_m);
    const pace = splitWeightPace(s, thresholdPaceSecPerKm);
    return Math.max(1, Math.round((dm / 1000) * pace));
  }
  return 1;
}

/**
 * @param {{
 *   intensity_norm?: number,
 *   cp_percent?: number | null | '',
 *   run_intensity_class?: string,
 *   rpe?: number,
 * }} s
 */
export function splitIntensityNorm(s) {
  if (typeof s.intensity_norm === 'number' && Number.isFinite(s.intensity_norm)) {
    return s.intensity_norm;
  }
  const cpRaw = s.cp_percent;
  const cp =
    cpRaw === '' || cpRaw == null ? NaN : Number(String(cpRaw).trim().replace(',', '.'));
  if (Number.isFinite(cp)) {
    if (cp <= 0) return 0.02;
    return Math.min(1.25, Math.max(0.35, cp / 100));
  }
  if (s.rpe != null && Number.isFinite(Number(s.rpe))) {
    return Math.min(1, Math.max(0.3, Number(s.rpe) / 10));
  }
  const ic = (s.run_intensity_class || 'work').toLowerCase();
  if (ic === 'rest') {
    const cpR = s.cp_percent;
    const cpN = cpR === '' || cpR == null ? NaN : Number(String(cpR).trim().replace(',', '.'));
    if (Number.isFinite(cpN) && cpN > 0) {
      return Math.min(1.25, Math.max(0.35, cpN / 100));
    }
    return 0.02;
  }
  if (ic === 'warmup' || ic === 'cooldown') return 0.72;
  return 0.85;
}

/** @param {Parameters<typeof splitIntensityNorm>[0]} s */
export function splitBarHeightPercent(s) {
  const norm = splitIntensityNorm(s);
  return Math.round(Math.min(100, Math.max(2, norm * 92)));
}

/**
 * Strong heatmap: saturated cool blue (0% CP) → vivid pink (150% CP).
 * HSL keeps mid-tones readable on white/light cards without pastel “washed” RGB pastels.
 */
export function cpPercentHeatmapColor(cp) {
  const n = Number(cp);
  const t = !Number.isFinite(n) ? 0 : Math.min(1, Math.max(0, n / GRAPHIC_EDITOR_CP_MAX));
  const h0 = 201;
  const h1 = 322;
  const s0 = 92;
  const s1 = 88;
  const l0 = 48;
  const l1 = 42;
  const h = h0 + (h1 - h0) * t;
  const s = s0 + (s1 - s0) * t;
  const l = l0 + (l1 - l0) * t;
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

/** True when the bar should use %CP heatmap (only explicit work-like types; not warmup/cooldown/rest). */
export function runningSegmentUsesCpHeatmapBarColor(intensityClass) {
  const c = (intensityClass || 'work').toLowerCase();
  return !['warmup', 'cooldown', 'rest'].includes(c);
}

/** Tailwind classes for non–heat-map segment bars (warmup / cooldown / rest). Empty for work. */
export function runningSegmentBarColorClass(intensityClass) {
  const c = (intensityClass || 'work').toLowerCase();
  if (c === 'warmup') return 'bg-emerald-600 dark:bg-emerald-500';
  if (c === 'cooldown') return 'bg-sky-600 dark:bg-sky-500';
  if (c === 'rest') return 'bg-muted-foreground/45';
  return '';
}

/**
 * Solid fill for chart/export contexts that need inline `backgroundColor`.
 * Returns null when the caller should use %CP heatmap instead.
 */
export function runningSegmentBarCssColor(intensityClass) {
  const c = (intensityClass || 'work').toLowerCase();
  if (c === 'warmup') return '#059669';
  if (c === 'cooldown') return '#0284c7';
  if (c === 'rest') return 'hsl(var(--muted-foreground) / 0.55)';
  return null;
}

/**
 * %CP (0–GRAPHIC_EDITOR_CP_MAX) for heatmap on a viz split row.
 * Aligns with graphicEditorEffectiveCp for rest / numeric cp; intensity proxy when cp is absent.
 */
export function vizSplitCpForHeatmap(s) {
  const ic = (s.run_intensity_class || 'work').toLowerCase();
  const cpRaw = s.cp_percent;
  const n = cpRaw === '' || cpRaw == null ? NaN : Number(String(cpRaw).trim().replace(',', '.'));
  if (ic === 'rest') {
    if (Number.isFinite(n) && n > 0) {
      return Math.min(GRAPHIC_EDITOR_CP_MAX, Math.max(0, n));
    }
    return 0;
  }
  if (Number.isFinite(n)) {
    return Math.min(GRAPHIC_EDITOR_CP_MAX, Math.max(0, n));
  }
  return Math.min(GRAPHIC_EDITOR_CP_MAX, Math.max(0, splitIntensityNorm(s) * 100));
}

/** Top fraction of each column used for reorder drag (rest is intensity band). */
export const GRAPHIC_EDITOR_GRAB_FRAC = 0.22;

export const GRAPHIC_EDITOR_INTENSITY_FRAC = 1 - GRAPHIC_EDITOR_GRAB_FRAC;

/**
 * Effective %CP for graphic editor fill (0 = floor / rest segment).
 * @param {object} seg row with run_intensity_class, cp_percent
 */
export function graphicEditorEffectiveCp(seg) {
  const ic = (seg.run_intensity_class || 'work').toLowerCase();
  const raw = seg.cp_percent;
  const n = raw === '' || raw == null ? NaN : Number(String(raw).trim().replace(',', '.'));
  if (ic === 'rest') {
    if (Number.isFinite(n) && n > 0) {
      return Math.min(GRAPHIC_EDITOR_CP_MAX, Math.max(0, n));
    }
    return 0;
  }
  if (!Number.isFinite(n)) return 0;
  return Math.min(GRAPHIC_EDITOR_CP_MAX, Math.max(0, n));
}

/** Colored bar height as % of the intensity sub-column (0–100). */
export function graphicEditorIntensityFillPercent(seg) {
  return (graphicEditorEffectiveCp(seg) / GRAPHIC_EDITOR_CP_MAX) * 100;
}

/** `bottom` % for the 100% CP dashed line on the full chart row (aligns across columns). */
export function graphicEditorCp100LineBottomPercentOfColumn() {
  return GRAPHIC_EDITOR_INTENSITY_FRAC * (100 / GRAPHIC_EDITOR_CP_MAX) * 100;
}

/**
 * Editor / API row with optional string fields → viz shape for shared weight + height.
 * @param {object} seg
 */
export function segmentRowToVizSplit(seg) {
  const duration_sec =
    seg.duration_sec === '' || seg.duration_sec == null ? 0 : Number(seg.duration_sec) || 0;
  const dm = seg.distance_m === '' || seg.distance_m == null ? undefined : Number(seg.distance_m);
  const distance_m = dm != null && Number.isFinite(dm) && dm > 0 ? dm : undefined;
  const cpRaw = seg.cp_percent;
  const cpParsed =
    cpRaw === '' || cpRaw == null ? NaN : Number(String(cpRaw).trim().replace(',', '.'));
  const cpNum = Number.isFinite(cpParsed) ? cpParsed : null;
  const sm = (seg.split_measure_mode || 'duration').toLowerCase() === 'distance' ? 'distance' : 'duration';
  const ddu =
    seg.distance_display_unit === 'miles' || seg.distance_display_unit === 'km' ? seg.distance_display_unit : undefined;
  return {
    duration_sec,
    distance_m,
    split_measure_mode: sm,
    distance_display_unit: ddu,
    run_intensity_class: (seg.run_intensity_class || 'work').toLowerCase(),
    cp_percent: cpNum,
    intensity_norm: undefined,
  };
}

export function splitBarOpacity(s) {
  return s.endless ? 0.2 : 1;
}

/**
 * Inline CSS color for a segment bar (resolves heatmap or static).
 * Returns null only when the segment uses Tailwind bg classes (non-heatmap non-rest).
 */
export function resolveSegmentBarCssColor(seg) {
  if (runningSegmentUsesCpHeatmapBarColor(seg.run_intensity_class)) {
    return cpPercentHeatmapColor(graphicEditorEffectiveCp(seg));
  }
  return runningSegmentBarCssColor(seg.run_intensity_class);
}

/**
 * Inline style for an endless element given a resolved CSS color string.
 * Translucent fill (20%) + crisp inset outline at full colour.
 */
export function endlessStyle(color) {
  if (!color) {
    return { opacity: 0.2, outline: '1.5px solid currentColor' };
  }
  return {
    backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)`,
    outline: `1.5px solid ${color}`,
    outlineOffset: '-1.5px',
  };
}

/**
 * Style props for an endless bar: translucent fill + crisp inset outline at full colour.
 * Returns { bgClass, style } where bgClass may override the Tailwind bg class.
 */
export function endlessBarProps(seg) {
  if (!seg.endless) return { bgClass: undefined, style: {} };
  const color = resolveSegmentBarCssColor(seg);
  if (!color) {
    return { bgClass: undefined, style: endlessStyle(null) };
  }
  return {
    bgClass: '',
    style: endlessStyle(color),
  };
}

export function splitTitleParts(s) {
  const cp = s.cp_percent;
  const cpPart =
    cp != null && cp !== '' && Number.isFinite(Number(cp)) ? `${cp}% CP` : null;
  const aw = s.average_watts;
  const wPart =
    aw != null && aw !== '' && Number.isFinite(Number(aw)) ? `${Math.round(Number(aw))} W` : null;
  return [s.run_intensity_class, s.duration_sec ? `${s.duration_sec}s` : null, cpPart, wPart]
    .filter(Boolean)
    .join(' · ');
}
